// Path-based routing with the History API. The worker serves index.html for every app path.

import { useEffect, useState } from 'preact/hooks';

type Guard = () => boolean | Promise<boolean>;

const listeners = new Set<() => void>();
const guards = new Set<Guard>();
const where = () => location.pathname + location.search;
let shown = where(); // the path the app is showing
let leaving = false; // a guard is still deciding

function notify(): void {
  shown = where();
  for (const l of listeners) l();
}

/** True if every leave-guard lets the page go. Sync when they all answer at once. */
function canLeave(): true | Promise<boolean> {
  const answers = [...guards].map((g) => g());
  if (answers.every((a) => a === true)) return true;
  return Promise.all(answers).then(
    (all) => all.every(Boolean),
    () => false,
  );
}

// Browser Back and Forward run the guards too. While a guard decides, the page stays on screen.
// If the guard says stay, the address goes back to the page that is showing.
window.addEventListener('popstate', () => {
  if (where() === shown) return;
  const ok = canLeave();
  if (ok === true) return notify();
  leaving = true;
  ok.then((yes) => {
    leaving = false;
    if (yes) notify();
    else history.pushState(null, '', shown);
  });
});

/**
 * Go to an app path. Runs leave-guards first (the editor uses one to finish saving).
 * `force` skips them: only for signing in again, where the editor keeps a copy on the device.
 */
export function navigate(to: string, opts: { replace?: boolean; keepScroll?: boolean; force?: boolean } = {}): void {
  if (to === where()) return;
  const go = () => {
    if (opts.replace) history.replaceState(null, '', to);
    else history.pushState(null, '', to);
    notify();
    if (!opts.keepScroll) window.scrollTo({ top: 0 });
  };
  if (opts.force) return go();
  if (leaving) return;
  const ok = canLeave();
  if (ok === true) return go();
  leaving = true;
  ok.then((yes) => {
    leaving = false;
    if (yes) go();
  });
}

/** A guard returns false (or a promise of false) to stop navigation. */
export function useLeaveGuard(guard: Guard): void {
  useEffect(() => {
    guards.add(guard);
    return () => {
      guards.delete(guard);
    };
  }, [guard]);
}

export function useLocation(): { path: string; query: URLSearchParams } {
  const [, set] = useState(0);
  useEffect(() => {
    const l = () => set((n) => n + 1);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  return { path: location.pathname, query: new URLSearchParams(location.search) };
}

/** match('/emails/:id', '/emails/4') -> { id: '4' } */
export function match(pattern: string, path: string): Record<string, string> | null {
  const a = pattern.split('/').filter(Boolean);
  const b = path.split('/').filter(Boolean);
  if (a.length !== b.length) return null;
  const out: Record<string, string> = {};
  for (let i = 0; i < a.length; i++) {
    if (a[i].startsWith(':')) out[a[i].slice(1)] = decodeURIComponent(b[i]);
    else if (a[i] !== b[i]) return null;
  }
  return out;
}

/** onClick for <a href> that keeps ctrl/cmd-click working. */
export function linkClick(e: MouseEvent, href: string): void {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  navigate(href);
}
